import * as React from "react";
import {useEffect, useState} from "react";
import {backend} from "../../../../../xconvert-backend";
import {authentication} from "../../../../../authentication";
import {DeferredNotification} from "../../../../../generated/api";
import ReactTable from "react-table";
import {formatDate} from "../../../../../format";
import {Icon} from "semantic-ui-react";
import {DeferredNotificationModal} from "../modals/DeferredNotificationModal";

export interface DeferredNotificationsProps {
    orderId: string;
}

export function DeferredNotifications(props: DeferredNotificationsProps) {

    const [notifications, setNotifications] = useState<DeferredNotification[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [selectedNotification, setSelectedNotification] = useState<DeferredNotification | null>(null);

    useEffect(() => {
        async function fetchNotifications() {
            setIsLoading(true);
            try {
                let auth = backend.withTokenAuthHeader(authentication.token);
                let response = await backend.internalApi.deferredNotifications(props.orderId, auth);
                setNotifications(response.deferredNotifications || []);
            } catch (error) {
                console.error("Error fetching deferred notifications:", error);
            } finally {
                setIsLoading(false);
            }
        }
        if (props.orderId) {
            fetchNotifications().then();
        }
    }, [props.orderId]);

    const columns = [
        {
            id: '_id',
            Header: 'ID',
            width: 200,
            accessor: (d: DeferredNotification) => d._id,
        },
        {
            id: 'created',
            Header: 'Created',
            width: 150,
            accessor: (d: DeferredNotification) => d.created,
            Cell: (d: any) => {
                return <span>{formatDate(d.value)}</span>
            },
        },
        {
            id: 'deferredUntil',
            Header: 'Deferred Until',
            width: 150,
            accessor: (d: DeferredNotification) => d.deferredUntil,
            Cell: (d: any) => {
                return <span>{formatDate(d.value)}</span>
            },
        },
        {
            id: 'type',
            Header: 'Type',
            width: 100,
            accessor: (d: DeferredNotification) => {
                if (d.emailNotification) {
                    return 'Email';
                } else if (d.smsNotification) {
                    return 'SMS';
                }
                return 'Unknown';
            },
            Cell: (d: any) => {
                const type = d.value;
                return <span>
                    {type === 'Email' && <Icon name="mail"/>}
                    {type === 'SMS' && <Icon name="mobile"/>}
                    {type}
                </span>
            },
        },
        {
            id: 'processed',
            Header: 'Processed',
            width: 100,
            accessor: (d: DeferredNotification) => d.processed,
            Cell: (d: any) => {
                if (d.value) {
                    return <span style={{color: 'green'}}><Icon name="check"/>Yes</span>
                } else {
                    return <span style={{color: 'orange'}}><Icon name="clock"/>Pending</span>
                }
            },
        },
        {
            id: 'sent',
            Header: 'Sent',
            width: 150,
            accessor: (d: DeferredNotification) => d.sent,
            Cell: (d: any) => {
                return <span>{formatDate(d.value)}</span>
            },
        },
        {
            id: 'errorCount',
            Header: 'Error Count',
            width: 100,
            accessor: (d: DeferredNotification) => d.errorCount || 0,
            Cell: (d: any) => {
                const count = d.value;
                if (count > 0) {
                    return <span style={{color: 'red', fontWeight: 'bold'}}>{count}</span>
                }
                return <span>{count}</span>
            },
        },
        {
            id: 'observerId',
            Header: 'Observer ID',
            width: 200,
            accessor: (d: DeferredNotification) => d.observerId,
        },
        {
            id: 'collapseKey',
            Header: 'Collapse Key',
            width: 150,
            accessor: (d: DeferredNotification) => d.collapseKey,
        },
    ];

    if (isLoading) {
        return <p>Loading deferred notifications...</p>
    }

    if (notifications.length === 0) {
        return <p>No deferred notifications found for this order.</p>
    }

    return <>
        <ReactTable
            data={notifications}
            columns={columns}
            defaultPageSize={notifications.length}
            className="-striped -highlight"
            loading={isLoading}
            style={{cursor: "pointer"}}
            showPagination={false}
            getTdProps={(state, rowInfo, column, instance) => {
                return {
                    onDoubleClick: () => {
                        if (rowInfo) {
                            setSelectedNotification(rowInfo.original);
                        }
                    }
                };
            }}
        />
        
        <DeferredNotificationModal
            isOpen={selectedNotification !== null}
            onClose={() => setSelectedNotification(null)}
            notification={selectedNotification}
        />
    </>

}